import React, { useRef } from 'react';
import PropTypes from 'prop-types';
import { observer } from 'mobx-react';

import { analytics } from 'core/analytics';

import { Icon, Menu } from 'components';
import Heading from 'components/Heading';
import Tooltip from 'components/Tooltip';
import UserSettings from 'libs/rendering/components/header/userSettings';

import { imageSrc } from 'apps/file/utils';
import { Avatar } from 'apps/components/shared';
import { conf as mainConf } from 'apps/config.yaml';

import {
  MainCollaboratorsWrapper,
  CircleWrapper,
  CirclesRowWrapper,
  TotalWrapper,
  IconWrapper,
  NoOtherCollaboratorsButton,
} from 'libs/rendering/components/header/collaborators.styled';

const ActivityIndicator = observer(props => {
  const { status, profile } = props?.activity;

  const tooltipContent =
    status === 'INVITED'
      ? `${profile.username} &#8212; invite sent.`
      : profile.username;

  const tooltipId = `activty-tooltip-${profile.id}`;

  return (
    <CircleWrapper
      data-tip={tooltipContent}
      data-for={tooltipId}
      status={status}
    >
      <Tooltip id={tooltipId} delayShow={500} html place='bottom' />
      <Avatar
        noOpacity
        src={imageSrc(
          profile.avatar,
          profile.imageFallbackPath,
          mainConf.imgSizes.profile.small,
        )}
      />
    </CircleWrapper>
  );
});

ActivityIndicator.propTypes = {
  activity: PropTypes.object,
};

const Collaborators = observer(props => {
  const { usersActivity, maxUsers, sharedFileManager } = props;
  const settingsIconRef = useRef(null);
  const slicedUsersList = usersActivity.slice(0, maxUsers);
  let content = null;

  const handleClick = () => {
    analytics.sendEvent('invite-share-file');
    analytics.sendEvent('invite-share-file-CAD-rooms');
    sharedFileManager.SetUserSettingsOpen(true);
  };

  const handleMenuClose = () => {
    sharedFileManager.SetUserSettingsOpen(false);
  };

  const handleCloseBtnClick = () => {
    sharedFileManager.SetUserSettingsOpen(false);
  };

  if (usersActivity.length === 1) {
    content = (
      <NoOtherCollaboratorsButton
        onClick={handleClick}
        ref={settingsIconRef}
        data-test-id='open-collaborator-menu-button'
      >
        Share
        <Icon name='user_plus' colorPath='wfWhite' size='default' />
      </NoOtherCollaboratorsButton>
    );
  } else {
    content = (
      <>
        <CirclesRowWrapper>
          {slicedUsersList.map((activity, index) => (
            <ActivityIndicator key={index} activity={activity} />
          ))}
        </CirclesRowWrapper>
        {usersActivity.length > maxUsers && (
          <TotalWrapper>
            <Tooltip id='tooltipTotal' delayShow={200} html place='bottom' />
            <Heading
              level='5'
              size='xsmall'
              data-tip='Total of users'
              data-for='tooltipTotal'
            >
              {usersActivity.length}
            </Heading>
          </TotalWrapper>
        )}
        <IconWrapper
          ref={settingsIconRef}
          onClick={handleClick}
          data-test-id='open-collaborator-menu-button'
        >
          <Icon name='user_settings' colorPath='wfWhite' />
        </IconWrapper>
      </>
    );
  }

  return (
    <MainCollaboratorsWrapper>
      {content}
      <Menu
        open={sharedFileManager.userSettingsOpen}
        anchorEl={settingsIconRef.current}
        onClose={handleMenuClose}
      >
        <UserSettings
          sharedFileManager={sharedFileManager}
          closeMenu={handleCloseBtnClick}
          usersActivity={usersActivity}
        />
      </Menu>
    </MainCollaboratorsWrapper>
  );
});

Collaborators.propTypes = {
  usersActivity: PropTypes.arrayOf(
    PropTypes.shape(ActivityIndicator.propTypes),
  ),
  maxUsers: PropTypes.number,
};

export default Collaborators;
